import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, fileUrl, downloadUrl, proofPdfUrl, albaranPdfUrl, decaPdfUrl, lookupPostal } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { ShipmentProgress } from "@/components/ShipmentProgress";
import RouteMap from "@/components/RouteMap";
import SignaturePad from "@/components/SignaturePad";
import { DOC_CATS, DETAIL_FIELDS, SERVICE_TYPES } from "@/pages/Shipments";
import { ArrowLeft, MapPin, Package, User, Truck, Weight, Calendar, Plus, Loader2, Trash2, FileText, Download, Upload, PenLine, Camera, CheckCircle2, Hash, Pencil, Tag } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const STATUS_OPTS = [
  { v: "pending", l: "Pendiente" },
  { v: "in_transit", l: "En tránsito" },
  { v: "delivered", l: "Entregado" },
  { v: "exception", l: "Incidencia" },
];

const DOT = {
  pending: "bg-amber-500", in_transit: "bg-blue-500", delivered: "bg-emerald-500", exception: "bg-red-500",
};

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

export default function ShipmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ev, setEv] = useState({ status: "in_transit", location: "", note: "" });
  const [docCat, setDocCat] = useState("albaran");
  const [docFile, setDocFile] = useState(null);
  const [docBusy, setDocBusy] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [proofNote, setProofNote] = useState("");
  const [proofPhoto, setProofPhoto] = useState(null);
  const [proofBusy, setProofBusy] = useState(false);
  const sigRef = useRef(null);
  const [drivers, setDrivers] = useState([]);
  const [clients, setClients] = useState([]);
  const [companySel, setCompanySel] = useState("");
  const [assign, setAssign] = useState({ tracking_number: "", assigned_driver_id: "" });
  const [assignBusy, setAssignBusy] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [ef, setEf] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const [cost, setCost] = useState("");
  const [costBusy, setCostBusy] = useState(false);
  const [collab, setCollab] = useState("");
  const [collabCost, setCollabCost] = useState("");
  const [collabList, setCollabList] = useState([]);
  const [internalNotes, setInternalNotes] = useState("");
  const [notesBusy, setNotesBusy] = useState(false);

  const load = () => api.get(`/shipments/${id}`).then((r) => setS(r.data)).catch(() => nav("/shipments"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { if (user.role === "admin") api.get("/users?role=driver").then((r) => setDrivers(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user.role === "admin") api.get("/users?role=client").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user.role === "admin") api.get("/collaborators").then((r) => setCollabList(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { if (s) { setAssign({ tracking_number: s.tracking_number || "", assigned_driver_id: s.assigned_driver_id || "" }); setCost(s.cost != null ? s.cost : ""); setCollab(s.collaborator || ""); setCollabCost(s.collaborator_cost != null ? s.collaborator_cost : ""); setInternalNotes(s.internal_notes || ""); } }, [s]);

  const addEvent = async () => {
    setBusy(true);
    try {
      await api.post(`/shipments/${id}/events`, ev);
      toast.success("Estado actualizado");
      setOpen(false);
      setEv({ status: "in_transit", location: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar este envío?")) return;
    await api.delete(`/shipments/${id}`);
    toast.success("Envío eliminado");
    nav("/shipments");
  };

  const assignShipment = async () => {
    if (assign.tracking_number && !assign.tracking_number.toUpperCase().startsWith("TM")) {
      toast.error("El número de seguimiento debe empezar por TM"); return;
    }
    setAssignBusy(true);
    try {
      await api.put(`/shipments/${id}`, {
        tracking_number: assign.tracking_number || null,
        assigned_driver_id: assign.assigned_driver_id || null,
      });
      toast.success("Asignación guardada");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setAssignBusy(false); }
  };

  const openEdit = () => {
    setCompanySel(s.client_company || (s.client_name ? "Sin empresa" : ""));
    setEf({
      client_id: s.client_id || "", client_nif: s.client_nif || "", service_type: s.service_type || "transporte", assigned_driver_id: s.assigned_driver_id || "",
      request_date: s.request_date || "",
      weight: s.weight ?? "", volume_m3: s.volume_m3 ?? "", reference: s.reference ?? "", cargo_nature: s.cargo_nature ?? "",
      pickup_estimated: s.pickup_estimated || "", estimated_delivery: s.estimated_delivery || "",
      pickup: { ...(s.pickup_details || {}) }, delivery: { ...(s.delivery_details || {}) },
      measures: (Array.isArray(s.measures) && s.measures.length ? s.measures.map((m) => ({ ...m })) : [{ length: "", width: "", height: "", packages: "" }]),
    });
    setEditOpen(true);
  };
  const efDetail = (sec, k, v) => setEf((f) => ({ ...f, [sec]: { ...f[sec], [k]: v } }));
  const efPostal = async (sec, v) => { efDetail(sec, "postal_code", v); const r = await lookupPostal(v); if (r) setEf((f) => ({ ...f, [sec]: { ...f[sec], postal_code: v, poblacion: f[sec].poblacion || r.poblacion, ciudad: f[sec].ciudad || r.ciudad } })); };
  const efVol = (measures) => { let v = 0; measures.forEach((m) => { const l = parseFloat(m.length), w = parseFloat(m.width), h = parseFloat(m.height), p = parseInt(m.packages) || 1; if (l > 0 && w > 0 && h > 0) v += (l * w * h) / 1000000 * p; }); return v; };
  const efMeasure = (i, k, v) => { const measures = ef.measures.map((m, idx) => idx === i ? { ...m, [k]: v } : m); const vol = efVol(measures); setEf({ ...ef, measures, volume_m3: vol > 0 ? Number(vol.toFixed(3)) : ef.volume_m3 }); };
  const efAdd = () => { if (ef.measures.length < 5) setEf({ ...ef, measures: [...ef.measures, { length: "", width: "", height: "", packages: "" }] }); };
  const efRemove = (i) => setEf({ ...ef, measures: ef.measures.filter((_, idx) => idx !== i) });
  const saveEdit = async () => {
    setEditBusy(true);
    try {
      const totalPackages = ef.measures.reduce((a, m) => a + (parseInt(m.packages) || 0), 0) || (s.packages || 1);
      await api.put(`/shipments/${id}/edit`, {
        client_id: ef.client_id || null, client_nif: ef.client_nif || null, service_type: ef.service_type || null,
        assigned_driver_id: ef.assigned_driver_id || null, request_date: ef.request_date || null,
        weight: parseFloat(ef.weight) || 0, packages: totalPackages, reference: ef.reference ?? "", cargo_nature: ef.cargo_nature ?? "",
        volume_m3: ef.volume_m3 !== "" && ef.volume_m3 != null ? parseFloat(ef.volume_m3) : null,
        measures: ef.measures.filter((m) => m.length || m.width || m.height || m.packages),
        pickup_estimated: ef.pickup_estimated || null, estimated_delivery: ef.estimated_delivery || null,
        pickup_details: ef.pickup, delivery_details: ef.delivery,
      });
      toast.success("Cambios guardados");
      setEditOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setEditBusy(false); }
  };

  const saveCost = async () => {
    setCostBusy(true);
    try {
      await api.put(`/shipments/${id}`, {
        cost: cost === "" ? null : parseFloat(cost),
        collaborator: collab.trim() === "" ? null : collab.trim(),
        collaborator_cost: collabCost === "" ? null : parseFloat(collabCost),
      });
      toast.success("Coste guardado");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCostBusy(false); }
  };

  const saveNotes = async () => {
    setNotesBusy(true);
    try {
      await api.put(`/shipments/${id}`, { internal_notes: internalNotes.trim() === "" ? null : internalNotes.trim() });
      toast.success("Observaciones internas guardadas");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setNotesBusy(false); }
  };

  const uploadDoc = async () => {    if (!docFile) { toast.error("Selecciona un archivo"); return; }
    setDocBusy(true);
    try {
      const fd = new FormData();
      fd.append("category", docCat);
      fd.append("file", docFile);
      await api.post(`/shipments/${id}/documents`, fd);
      toast.success("Documento subido");
      setDocFile(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setDocBusy(false); }
  };

  const submitProof = async () => {
    const sigBlob = await new Promise((res) => {
      if (sigRef.current && !sigRef.current.isEmpty()) sigRef.current.toBlob((b) => res(b));
      else res(null);
    });
    if (!sigBlob && !proofPhoto) { toast.error("Añade una firma o una foto"); return; }
    setProofBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", proofNote);
      if (proofPhoto) fd.append("photo", proofPhoto);
      if (sigBlob) fd.append("signature", new File([sigBlob], "firma.png", { type: "image/png" }));
      await api.post(`/shipments/${id}/delivery-proof`, fd);
      toast.success("Entrega confirmada");
      setProofOpen(false); setProofNote(""); setProofPhoto(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setProofBusy(false); }
  };

  if (!s) return <Layout><div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-freight" /></div></Layout>;

  const events = [...(s.tracking_events || [])].reverse();
  const canUpdate = user.role === "admin" || user.role === "driver";
  const inputCls = "w-full bg-background border border-input rounded-none px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-freight";

  const Info = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3">
      <Icon className="w-4 h-4 text-freight mt-0.5" />
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-sm font-medium">{value || "—"}</div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-4xl">
        <button data-testid="back-btn" onClick={() => nav("/shipments")} className="flex items-center gap-2 text-sm font-bold uppercase text-muted-foreground hover:text-freight mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Volver
        </button>

        <div className="bg-card border border-border p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="font-mono text-2xl font-bold text-freight" data-testid="detail-tracking">{s.tracking_number || "SIN ASIGNAR"}</div>
              <div className="text-muted-foreground mt-1">{s.description}</div>
              <div className="mt-3"><StatusBadge status={s.status} testid="detail-status" /></div>
            </div>
            <div className="flex items-center gap-3">
              {user.role === "admin" && (
                <a data-testid="albaran-btn" href={albaranPdfUrl(id)} target="_blank" rel="noopener noreferrer"
                  className="bg-corp hover:bg-corp-hover text-white flex items-center gap-1 text-sm font-bold uppercase px-3 py-1.5 transition-colors">
                  <FileText className="w-4 h-4" /> Crear albarán
                </a>
              )}
              {user.role === "admin" && (
                <a data-testid="deca-btn" href={decaPdfUrl(id)} target="_blank" rel="noopener noreferrer"
                  className="bg-freight hover:bg-freight/90 text-black flex items-center gap-1 text-sm font-bold uppercase px-3 py-1.5 transition-colors">
                  <FileText className="w-4 h-4" /> DeCA
                </a>
              )}
              {(user.role === "admin" || (user.role === "client" && s.client_id === user.id)) && (
                <button data-testid="edit-shipment-btn" onClick={openEdit} className="text-corp hover:underline flex items-center gap-1 text-sm font-bold uppercase"><Pencil className="w-4 h-4" /> Editar</button>
              )}
              {user.role === "admin" && (
                <button data-testid="delete-shipment-btn" onClick={remove} className="text-red-500 hover:text-red-600 flex items-center gap-1 text-sm font-bold uppercase">
                  <Trash2 className="w-4 h-4" /> Eliminar
                </button>
              )}
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-border">
            <ShipmentProgress status={s.status} />
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6 pt-6 border-t border-border">
            <Info icon={User} label="Cliente" value={s.client_name} />
            {s.client_company ? <Info icon={User} label="Empresa" value={s.client_company} /> : null}
            <Info icon={Tag} label="Servicio" value={(SERVICE_TYPES.find((t) => t.k === s.service_type) || { l: "Transporte" }).l} />
            <Info icon={Truck} label="Conductor" value={s.driver_name} />
            <Info icon={Hash} label="Referencia" value={s.reference} />
            <Info icon={Package} label="Naturaleza" value={s.cargo_nature} />
            <Info icon={MapPin} label="Origen" value={s.origin} />
            <Info icon={MapPin} label="Destino" value={s.destination} />
            <Info icon={Weight} label="Peso" value={s.weight ? `${s.weight} kg` : "—"} />
            <Info icon={Package} label="Bultos" value={s.packages} />
            <Info icon={Calendar} label="Entrega estimada" value={s.estimated_delivery} />
          </div>
        </div>

        {user.role === "admin" && (
          <div className="bg-corp/5 border border-corp/40 p-6 mb-6">
            <h2 className="font-display text-lg font-black uppercase tracking-tight mb-3 flex items-center gap-2"><Hash className="w-5 h-5 text-corp" /> Asignación</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase text-muted-foreground">Número de seguimiento (TM)</label>
                <input data-testid="assign-tracking" className={inputCls} placeholder="TM-XXXXXX"
                  value={assign.tracking_number} onChange={(e) => setAssign({ ...assign, tracking_number: e.target.value })} />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-muted-foreground">Conductor</label>
                <select data-testid="assign-driver" className={inputCls}
                  value={assign.assigned_driver_id} onChange={(e) => setAssign({ ...assign, assigned_driver_id: e.target.value })}>
                  <option value="">Sin asignar</option>
                  {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
            </div>
            <button data-testid="save-assign-btn" onClick={assignShipment} disabled={assignBusy}
              className="mt-3 bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 py-2 flex items-center gap-2 transition-colors disabled:opacity-60">
              {assignBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar asignación
            </button>
          </div>
        )}

        {ef && (
          <Dialog open={editOpen} onOpenChange={setEditOpen}>
            <DialogContent className="rounded-none bg-card max-w-2xl">
              <DialogHeader><DialogTitle className="font-display font-black uppercase">Editar Envío</DialogTitle></DialogHeader>
              <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Cliente (empresa)</label>
                    <select data-testid="edit-ship-company" className={inputCls} value={companySel}
                      onChange={(e) => { setCompanySel(e.target.value); setEf({ ...ef, client_id: "" }); }}>
                      <option value="">Seleccionar empresa</option>
                      {[...new Set(clients.map((c) => (c.company || "").trim() || "Sin empresa"))].sort().map((co) => <option key={co} value={co}>{co}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Operativo (quien solicita)</label>
                    <select data-testid="edit-ship-operativo" className={inputCls} value={ef.client_id} disabled={!companySel}
                      onChange={(e) => setEf({ ...ef, client_id: e.target.value })}>
                      <option value="">Seleccionar operativo</option>
                      {clients.filter((c) => ((c.company || "").trim() || "Sin empresa") === companySel).map((o) => <option key={o.id} value={o.id}>{o.name}{o.email ? ` (${o.email})` : ""}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">NIF/CIF cliente (DeCA)</label>
                    <input data-testid="edit-ship-client-nif" className={inputCls} value={ef.client_nif} placeholder="Ej: B12345678" onChange={(e) => setEf({ ...ef, client_nif: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Tipo de servicio</label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {SERVICE_TYPES.map((t) => (
                      <button key={t.k} type="button" data-testid={`edit-service-${t.k}`} onClick={() => setEf({ ...ef, service_type: t.k })}
                        className={`px-3 py-1.5 text-xs font-bold uppercase border transition-colors ${ef.service_type === t.k ? "bg-freight border-freight text-black" : "border-input text-muted-foreground hover:border-freight hover:text-freight"}`}>
                        {t.l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Conductor asignado</label>
                  <select data-testid="edit-ship-driver" className={inputCls} value={ef.assigned_driver_id} onChange={(e) => setEf({ ...ef, assigned_driver_id: e.target.value })}>
                    <option value="">Sin asignar</option>
                    {drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Referencia (facturación)</label>
                  <input data-testid="edit-reference" className={inputCls} value={ef.reference} onChange={(e) => setEf({ ...ef, reference: e.target.value })} />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase text-muted-foreground">Naturaleza de la mercancía</label>
                  <input data-testid="edit-cargo-nature" className={inputCls} value={ef.cargo_nature} onChange={(e) => setEf({ ...ef, cargo_nature: e.target.value })} placeholder="Ej: Palets de material eléctrico" />
                </div>
                <div className="border border-border p-3 space-y-2.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Datos de la mercancía</div>
                    <div className="w-28"><label className="text-[10px] font-bold uppercase text-muted-foreground">Kilos</label><input data-testid="edit-weight" type="number" className={inputCls} value={ef.weight} onChange={(e) => setEf({ ...ef, weight: e.target.value })} /></div>
                  </div>
                  <div className="space-y-2">
                    <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 text-[10px] font-bold uppercase text-muted-foreground"><span>Largo</span><span>Ancho</span><span>Alto</span><span>Bultos</span><span></span></div>
                    {ef.measures.map((m, i) => (
                      <div key={i} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-2 items-center">
                        <input type="number" className={inputCls} value={m.length} onChange={(e) => efMeasure(i, "length", e.target.value)} />
                        <input type="number" className={inputCls} value={m.width} onChange={(e) => efMeasure(i, "width", e.target.value)} />
                        <input type="number" className={inputCls} value={m.height} onChange={(e) => efMeasure(i, "height", e.target.value)} />
                        <input type="number" className={inputCls} value={m.packages} onChange={(e) => efMeasure(i, "packages", e.target.value)} />
                        {ef.measures.length > 1 ? <button type="button" onClick={() => efRemove(i)} className="text-red-500 flex justify-center"><Trash2 className="w-4 h-4" /></button> : <span />}
                      </div>
                    ))}
                    {ef.measures.length < 5 && <button type="button" onClick={efAdd} className="text-xs font-bold uppercase text-corp hover:underline">+ Añadir línea</button>}
                  </div>
                  <div><label className="text-[10px] font-bold uppercase text-muted-foreground">Volumen cúbico (m³)</label><input data-testid="edit-volume" type="number" className={inputCls} value={ef.volume_m3} onChange={(e) => setEf({ ...ef, volume_m3: e.target.value })} /></div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  {[["pickup", "Recogida"], ["delivery", "Entrega"]].map(([sec, title]) => (
                    <div key={sec} className="border border-border p-3 space-y-2">
                      <div className="text-xs font-bold uppercase tracking-wider text-corp">{title}</div>
                      {DETAIL_FIELDS.map((f) => (
                        <div key={f.k}>
                          <label className="text-[10px] font-bold uppercase text-muted-foreground">{f.l}</label>
                          {f.k === "notes" ? (
                            <textarea className={inputCls} value={ef[sec][f.k] || ""} onChange={(e) => efDetail(sec, f.k, e.target.value)} />
                          ) : (
                            <input data-testid={`edit-${sec}-${f.k}`} className={inputCls} value={ef[sec][f.k] || ""} onChange={(e) => f.k === "postal_code" ? efPostal(sec, e.target.value) : efDetail(sec, f.k, e.target.value)} />
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="text-xs font-bold uppercase text-muted-foreground">Fecha de petición</label><input data-testid="edit-request-date" type="date" className={inputCls} value={ef.request_date} onChange={(e) => setEf({ ...ef, request_date: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-muted-foreground">Fecha del servicio (recogida)</label><input type="date" className={inputCls} value={ef.pickup_estimated} onChange={(e) => setEf({ ...ef, pickup_estimated: e.target.value })} /></div>
                  <div><label className="text-xs font-bold uppercase text-muted-foreground">Entrega estimada</label><input type="date" className={inputCls} value={ef.estimated_delivery} onChange={(e) => setEf({ ...ef, estimated_delivery: e.target.value })} /></div>
                </div>
              </div>
              <DialogFooter>
                <button data-testid="save-edit-btn" onClick={saveEdit} disabled={editBusy} className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-60">{editBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar cambios</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {(s.pickup_details || s.delivery_details || s.volume_m3 != null || (Array.isArray(s.measures) && s.measures.length) || s.pickup_estimated) && (
          <div className="bg-card border border-border p-6 mb-6">
            <h2 className="font-display text-xl font-black uppercase tracking-tight mb-4">Datos de Recogida y Entrega</h2>
            <div className="mb-4 text-sm font-mono flex flex-wrap gap-x-6 gap-y-1">
              <span>Bultos: {s.packages}</span>
              <span>Kilos: {s.weight || 0}</span>
              {s.volume_m3 != null && <span>Volumen: {s.volume_m3} m³</span>}
              {s.pickup_estimated && <span>Recogida est.: {s.pickup_estimated}</span>}
              {s.estimated_delivery && <span>Entrega est.: {s.estimated_delivery}</span>}
            </div>
            {Array.isArray(s.measures) && s.measures.length > 0 && (
              <div className="mb-4 text-xs font-mono border border-border max-w-md">
                <div className="grid grid-cols-4 gap-2 px-2 py-1 bg-muted font-bold uppercase text-[10px] text-muted-foreground"><span>Largo</span><span>Ancho</span><span>Alto</span><span>Bultos</span></div>
                {s.measures.map((m, i) => (<div key={i} className="grid grid-cols-4 gap-2 px-2 py-1 border-t border-border"><span>{m.length || "-"}</span><span>{m.width || "-"}</span><span>{m.height || "-"}</span><span>{m.packages || "-"}</span></div>))}
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              {[["Recogida", s.pickup_details], ["Entrega", s.delivery_details]].map(([title, d]) => (
                <div key={title}>
                  <h3 className="font-display font-black uppercase text-sm tracking-wide mb-2 text-corp">{title}</h3>
                  {d ? (
                    <dl className="text-sm space-y-1">
                      {[["company", "Empresa"], ["address", "Dirección"], ["postal_code", "C.P."], ["poblacion", "Población"], ["ciudad", "Ciudad"], ["schedule", "Horario"], ["contact_name", "Contacto"], ["contact_phone", "Teléfono"], ["reference", "Referencia"], ["notes", "Observaciones"]].map(([k, l]) => d[k] ? (
                        <div key={k} className="flex gap-2"><dt className="text-muted-foreground min-w-[90px]">{l}:</dt><dd className="font-medium">{d[k]}</dd></div>
                      ) : null)}
                    </dl>
                  ) : <p className="text-muted-foreground text-sm">—</p>}
                </div>
              ))}
            </div>
          </div>
        )}

        {(user.role === "admin" || s.cost != null) && (
          <div className="bg-card border border-border p-6 mb-6" data-testid="cost-section">
            <h2 className="font-display text-xl font-black uppercase tracking-tight mb-4">Coste del Servicio</h2>
            {user.role === "admin" ? (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1 max-w-xs">
                    <label className="text-[10px] font-bold uppercase text-muted-foreground">Coste (€) — visible para el cliente</label>
                    <input data-testid="cost-input" type="number" step="0.01" className={inputCls} value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" />
                  </div>
                </div>
                <div className="border-t border-border pt-3">
                  <p className="text-[10px] font-bold uppercase text-freight mb-2">Colaborador (solo administración)</p>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                    <div className="flex-1 max-w-xs">
                      <label className="text-[10px] font-bold uppercase text-muted-foreground">Empresa colaboradora</label>
                      <input data-testid="collaborator-input" type="text" list="collaborator-options" className={inputCls} value={collab} onChange={(e) => setCollab(e.target.value)} placeholder="Empresa subcontratada" />
                      <datalist id="collaborator-options">
                        {collabList.map((c) => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                    <div className="flex-1 max-w-xs">
                      <label className="text-[10px] font-bold uppercase text-muted-foreground">Coste colaborador (€)</label>
                      <input data-testid="collaborator-cost-input" type="number" step="0.01" className={inputCls} value={collabCost} onChange={(e) => setCollabCost(e.target.value)} placeholder="0.00" />
                    </div>
                  </div>
                </div>
                <button data-testid="save-cost-btn" onClick={saveCost} disabled={costBusy}
                  className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 py-2 flex items-center gap-2 transition-colors disabled:opacity-60">
                  {costBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
                </button>
              </div>
            ) : (
              <div data-testid="cost-view" className="font-display text-3xl font-black text-corp">{s.cost != null ? `${s.cost} €` : "—"}</div>
            )}
          </div>
        )}

        <div className="bg-card border border-border p-6 mb-6">
          <h2 className="font-display text-xl font-black uppercase tracking-tight mb-4">Ruta</h2>
          <RouteMap origin={s.origin} destination={s.destination} />
          <div className="flex items-center justify-between mt-3 text-xs flex-wrap gap-2">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-freight inline-block" /> {s.origin}</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-corp inline-block" /> {s.destination}</span>
          </div>
        </div>

        {(user.role === "admin" || user.role === "client") && (
          <div className="bg-card border border-border p-6 mb-6">
            <h2 className="font-display text-xl font-black uppercase tracking-tight mb-4">Documentos</h2>
            {(s.documents && s.documents.length > 0) ? (
              <div className="space-y-3 mb-5">
                {DOC_CATS.map((c) => {
                  const docs = (s.documents || []).filter((d) => d.category === c.key);
                  if (!docs.length) return null;
                  return (
                    <div key={c.key}>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5">{c.label}</div>
                      <div className="space-y-1.5">
                        {docs.map((d) => (
                          <a key={d.file_id} href={fileUrl(d.file_id)} target="_blank" rel="noreferrer" data-testid={`doc-${d.file_id}`}
                            className="flex items-center justify-between gap-2 border border-border px-3 py-2 hover:border-freight transition-colors group">
                            <span className="flex items-center gap-2 text-sm truncate"><FileText className="w-4 h-4 text-freight shrink-0" /><span className="truncate">{d.filename}</span></span>
                            <Download className="w-4 h-4 text-muted-foreground group-hover:text-freight shrink-0" />
                          </a>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : <p className="text-muted-foreground text-sm mb-5">Sin documentos adjuntos.</p>}

            <div className="border-t border-border pt-4 flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <label className="text-[10px] font-bold uppercase text-muted-foreground">Tipo de documento</label>
                <select data-testid="doc-category" className={inputCls} value={docCat} onChange={(e) => setDocCat(e.target.value)}>
                  {DOC_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <input data-testid="doc-file" type="file" onChange={(e) => setDocFile(e.target.files[0])}
                className="text-xs file:mr-2 file:border file:border-input file:bg-muted file:px-2 file:py-1.5 file:text-xs file:font-bold file:uppercase file:cursor-pointer" />
              <button data-testid="upload-doc-btn" onClick={uploadDoc} disabled={docBusy}
                className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 py-2 flex items-center gap-2 transition-colors disabled:opacity-60">
                {docBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Subir
              </button>
            </div>
          </div>
        )}

        <div className="bg-card border border-border p-6 mb-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <h2 className="font-display text-xl font-black uppercase tracking-tight">Prueba de Entrega</h2>
            {canUpdate && !s.delivery_proof && (
              <Dialog open={proofOpen} onOpenChange={setProofOpen}>
                <DialogTrigger asChild>
                  <button data-testid="delivery-proof-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-xs px-3 py-2 flex items-center gap-1.5 transition-colors">
                    <CheckCircle2 className="w-4 h-4" /> Confirmar entrega
                  </button>
                </DialogTrigger>
                <DialogContent className="rounded-none bg-card">
                  <DialogHeader><DialogTitle className="font-display font-black uppercase">Confirmar Entrega</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-1 mb-1"><PenLine className="w-3 h-3" /> Firma del receptor</label>
                      <SignaturePad ref={sigRef} />
                      <button type="button" onClick={() => sigRef.current && sigRef.current.clear()} className="text-xs text-muted-foreground hover:text-freight mt-1 transition-colors">Borrar firma</button>
                    </div>
                    <div>
                      <label className="text-[10px] font-bold uppercase text-muted-foreground flex items-center gap-1 mb-1"><Camera className="w-3 h-3" /> Foto de entrega</label>
                      <input data-testid="proof-photo" type="file" accept="image/*" capture="environment" onChange={(e) => setProofPhoto(e.target.files[0])}
                        className="text-xs w-full file:mr-2 file:border file:border-input file:bg-muted file:px-2 file:py-1.5 file:text-xs file:font-bold file:uppercase file:cursor-pointer" />
                    </div>
                    <textarea data-testid="proof-note" className={inputCls} placeholder="Nota (opcional): nombre de quien recibe..." value={proofNote} onChange={(e) => setProofNote(e.target.value)} />
                  </div>
                  <DialogFooter>
                    <button data-testid="save-proof-btn" onClick={submitProof} disabled={proofBusy}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 transition-colors disabled:opacity-60">
                      {proofBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
                    </button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          {s.delivery_proof ? (
            <div className="space-y-3" data-testid="delivery-proof-view">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm text-muted-foreground">Entregado por <span className="font-semibold text-foreground">{s.delivery_proof.signed_by}</span> · <span className="font-mono">{fmt(s.delivery_proof.delivered_at)}</span></div>
                <a data-testid="download-proof-btn" href={downloadUrl(s.delivery_proof.photo_file_id || s.delivery_proof.signature_file_id)}
                  className="bg-corp hover:bg-corp-hover text-white font-bold uppercase text-xs px-3 py-2 flex items-center gap-1.5 transition-colors">
                  <Download className="w-4 h-4" /> Descargar comprobante
                </a>
                <a data-testid="download-proof-pdf-btn" href={proofPdfUrl(id)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold uppercase text-xs px-3 py-2 flex items-center gap-1.5 transition-colors">
                  <FileText className="w-4 h-4" /> Comprobante PDF
                </a>
              </div>
              {s.delivery_proof.note && <div className="text-sm">{s.delivery_proof.note}</div>}
              <div className="grid grid-cols-2 gap-4">
                {s.delivery_proof.signature_file_id && (
                  <div><div className="text-[10px] font-bold uppercase text-muted-foreground mb-1">Firma</div>
                    <img src={fileUrl(s.delivery_proof.signature_file_id)} alt="Firma" className="border border-border bg-white max-h-40 object-contain w-full" />
                    <a data-testid="download-signature-btn" href={downloadUrl(s.delivery_proof.signature_file_id)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold uppercase text-corp hover:underline"><Download className="w-3.5 h-3.5" /> Descargar firma</a></div>
                )}
                {s.delivery_proof.photo_file_id && (
                  <div><div className="text-[10px] font-bold uppercase text-muted-foreground mb-1">Foto</div>
                    <img src={fileUrl(s.delivery_proof.photo_file_id)} alt="Foto de entrega" className="border border-border max-h-40 object-cover w-full" />
                    <a data-testid="download-photo-btn" href={downloadUrl(s.delivery_proof.photo_file_id)} className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold uppercase text-corp hover:underline"><Download className="w-3.5 h-3.5" /> Descargar foto</a></div>
                )}
              </div>
            </div>
          ) : <p className="text-muted-foreground text-sm">Aún no se ha confirmado la entrega.</p>}
        </div>

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-black uppercase tracking-tight">Historial de Seguimiento</h2>
          {canUpdate && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="add-event-btn" className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-xs px-3 py-2 flex items-center gap-1.5 transition-colors">
                  <Plus className="w-4 h-4" /> Actualizar estado
                </button>
              </DialogTrigger>
              <DialogContent className="rounded-none bg-card">
                <DialogHeader><DialogTitle className="font-display font-black uppercase">Actualizar Estado</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <select data-testid="event-status" className={inputCls} value={ev.status} onChange={(e) => setEv({ ...ev, status: e.target.value })}>
                    {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                  <input data-testid="event-location" className={inputCls} placeholder="Ubicación actual" value={ev.location} onChange={(e) => setEv({ ...ev, location: e.target.value })} />
                  <textarea data-testid="event-note" className={inputCls} placeholder="Nota (opcional)" value={ev.note} onChange={(e) => setEv({ ...ev, note: e.target.value })} />
                </div>
                <DialogFooter>
                  <button data-testid="save-event-btn" onClick={addEvent} disabled={busy} className="bg-freight hover:bg-freight-hover text-black font-bold uppercase text-sm px-4 py-2.5 flex items-center gap-2 disabled:opacity-60">
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
                  </button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <div className="bg-card border border-border p-6">
          {events.length === 0 ? (
            <p className="text-muted-foreground text-sm">Sin eventos.</p>
          ) : (
            <ol className="relative">
              {events.map((e, i) => (
                <li key={i} className="relative pl-8 pb-6 last:pb-0" data-testid={`timeline-event-${i}`}>
                  {i !== events.length - 1 && <span className="absolute left-[7px] top-4 bottom-0 w-px bg-border" />}
                  <span className={`absolute left-0 top-1 w-4 h-4 rounded-full border-2 border-card ${DOT[e.status] || "bg-neutral-400"}`} />
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusBadge status={e.status} />
                    <span className="font-mono text-xs text-muted-foreground">{fmt(e.timestamp)}</span>
                  </div>
                  {e.location && <div className="text-sm font-medium mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 text-freight" />{e.location}</div>}
                  {e.note && <div className="text-sm text-muted-foreground mt-0.5">{e.note}</div>}
                </li>
              ))}
            </ol>
          )}
        </div>

        {user.role === "admin" && (
          <div className="bg-card border border-border p-6 mt-6" data-testid="internal-notes-section">
            <h2 className="font-display text-xl font-black uppercase tracking-tight mb-1">Observaciones Internas</h2>
            <p className="text-[10px] font-bold uppercase text-freight mb-3">Solo administración · no aparece en albarán ni comprobantes · se vuelca al CSV</p>
            <textarea data-testid="internal-notes-input" rows={4} className={inputCls} value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} placeholder="Notas privadas para administración..." />
            <button data-testid="save-internal-notes-btn" onClick={saveNotes} disabled={notesBusy}
              className="mt-3 bg-corp hover:bg-corp-hover text-white font-bold uppercase text-sm px-4 py-2 flex items-center gap-2 transition-colors disabled:opacity-60">
              {notesBusy && <Loader2 className="w-4 h-4 animate-spin" />} Guardar observaciones
            </button>
          </div>
        )}
      </div>
    </Layout>
  );
}
